import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject as RxJsSubject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import {
  DataColumn,
  DataTableComponent,
  DataTablePage,
} from '../../../ui/data-table/data-table.component';
import { FilterBarComponent } from '../../../ui/filter-bar/filter-bar.component';
import { ModalComponent } from '../../../ui/modal/modal.component';
import { RegistrationStatus } from '../../../../core/interfaces/registration';
import {
  RegistrationCounts,
  AdminRegistrationDetail,
  AdminRegistrationListItem,
} from '../../../../core/interfaces/registration-admin';
import { RegistrationAdminService } from '../../../../core/services/registration-admin.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { ReadOnlyNoticeComponent } from '../../../ui/read-only-notice/read-only-notice.component';

import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
type StatusFilter = RegistrationStatus | 'all';

const PAGE_SIZES = [25, 50, 100];

/**
 * File de validation des inscriptions en ligne.
 *
 * <p>Une demande arrive du site vitrine avec un compte déjà créé mais inactif.
 * L'administrateur y contrôle l'ordre de virement déposé par l'élève, puis
 * valide — ce qui ouvre l'accès — ou rejette avec un motif, que l'élève verra
 * pour redéposer une pièce corrigée.</p>
 */
@Component({
  selector: 'app-registrations',
  standalone: true,
  imports: [
    LocalizedDatePipe,
    CommonModule,
    FormsModule,
    TranslateModule,
    DataTableComponent,
    FilterBarComponent,
    ModalComponent,
    ReadOnlyNoticeComponent,
  ],
  templateUrl: './registrations.component.html',
  styleUrl: './registrations.component.css',
})
export class RegistrationsComponent implements OnInit, OnDestroy {
  private readonly registrations = inject(RegistrationAdminService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly destroy$ = new RxJsSubject<void>();

  /**
   * Valider, rejeter, dispenser et renvoyer l'activation sont des décisions : lire les demandes ne les donne pas. Sans `registrations.write`, l'écran s'ouvre en consultation : champs
   * figés, gestes d'écriture retirés, et un bandeau qui le dit. Le back
   * revérifie : ce test n'est pas la protection, c'est l'honnêteté de l'écran.
   */
  readonly canWrite = inject(AuthService).hasPermission('registrations.write');

  rows: AdminRegistrationListItem[] = [];
  isLoading = false;
  hasError = false;

  /** Par défaut : ce qui attend une décision. Le reste est à un clic. */
  statusFilter: StatusFilter = 'awaiting_review';
  search = '';

  /**
   * Position dans la file. Le serveur pagine : sans cela l'écran n'en montrait
   * que les 25 premières demandes, sans rien pour atteindre les suivantes — la
   * pastille annonçait 60 demandes en attente, la liste en proposait 25.
   */
  page = 1;
  pageSize = PAGE_SIZES[0];
  readonly pageSizes = PAGE_SIZES;

  detail: AdminRegistrationDetail | null = null;
  isDetailOpen = false;
  isDetailLoading = false;
  isActing = false;

  /** Saisie de motif partagée par le rejet et l'exonération. */
  reason = '';
  pendingDecision: 'reject' | 'waive' | null = null;

  readonly statuses: StatusFilter[] = [
    'awaiting_review',
    'pending',
    'rejected',
    'validated',
    'all',
  ];

  readonly columns: DataColumn[] = [
    { key: 'reference', labelKey: 'REGISTRATIONS.COL_REFERENCE', width: '130px' },
    { key: 'name', labelKey: 'REGISTRATIONS.COL_NAME' },
    { key: 'email', labelKey: 'REGISTRATIONS.COL_EMAIL', hideBelow: 'lg' },
    { key: 'pack', labelKey: 'REGISTRATIONS.COL_PACK', hideBelow: 'md' },
    { key: 'amount', labelKey: 'REGISTRATIONS.COL_AMOUNT', numeric: true, align: 'end' },
    { key: 'status', labelKey: 'REGISTRATIONS.COL_STATUS', align: 'center', width: '150px' },
    { key: 'actions', labelKey: 'REGISTRATIONS.COL_ACTIONS', action: true, width: '110px' },
  ];

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Nombre de demandes par statut, pour les pastilles des facettes.
   *
   * <p>Impossible a deriver de la liste : le filtrage est fait par le serveur,
   * qui ne renvoie que le statut courant. Une facette annoncerait alors le
   * total de sa voisine.</p>
   */
  counts: RegistrationCounts | null = null;

  /** Compte affiche sur une facette ; `null` tant qu'il n'est pas connu. */
  countFor(status: StatusFilter): number | null {
    const c = this.counts;
    if (!c) return null;

    switch (status) {
      case 'awaiting_review': return c.awaitingReview;
      case 'pending': return c.pending;
      case 'rejected': return c.rejected;
      case 'validated': return c.validated;
      case 'all': return c.total;
      default: return null;
    }
  }

  /**
   * État de pagination de la table.
   *
   * <p>Le total vient des compteurs par statut, calculés par le serveur avec
   * la même recherche. S'ils manquent (leur échec est toléré), on déduit
   * seulement s'il reste une page : une page pleine en annonce une suivante.</p>
   */
  get pagination(): DataTablePage {
    const total = this.countFor(this.statusFilter);
    if (total !== null) {
      return {
        currentPage: this.page,
        totalPages: Math.max(1, Math.ceil(total / this.pageSize)),
        total,
      };
    }
    const mayHaveNext = this.rows.length >= this.pageSize && this.page !== this.lastKnownPage;
    return {
      currentPage: this.page,
      totalPages: mayHaveNext ? this.page + 1 : this.page,
      total: this.rows.length,
    };
  }

  get showPager(): boolean {
    return this.pagination.totalPages > 1 || this.pageSize !== PAGE_SIZES[0];
  }

  /**
   * Dernière page connue, apprise en tombant sur une page vide. Sans elle, une
   * file d'exactement une page pleine proposerait « suivant » indéfiniment
   * quand les compteurs manquent.
   */
  private lastKnownPage: number | null = null;

  /** Requêtes en cours — une seule de chaque à la fois. */
  private listRequest?: Subscription;
  private countsRequest?: Subscription;

  private loadCounts(): void {
    this.countsRequest?.unsubscribe();
    this.countsRequest = this.registrations
      .getCounts(this.search)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => (this.counts = res?.data ?? null),
        // Les pastilles sont un confort : leur echec ne doit pas priver
        // l'ecran de sa liste.
        error: () => (this.counts = null),
      });
  }

  /**
   * Relit la file **et** ses compteurs : à l'ouverture, après une décision,
   * quand la recherche change. Tourner une page ou changer de statut ne
   * change pas les compteurs — voir {@link loadPage}.
   */
  load(): void {
    this.loadCounts();
    this.loadPage();
  }

  private loadPage(): void {
    // Changer de statut pendant qu'une recherche est en vol lançait deux
    // requêtes : la plus lente gagnait, et la liste n'était plus celle de la
    // facette surlignée. À annuler avant de poser `isLoading`, que le
    // `finalize` de la requête abandonnée relâche.
    this.listRequest?.unsubscribe();
    this.isLoading = true;
    this.hasError = false;

    this.listRequest = this.registrations
      .search(
        this.statusFilter === 'all' ? null : this.statusFilter,
        this.search,
        this.page,
        this.pageSize,
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (res) => {
          this.rows = res.data ?? [];
          // La dernière demande d'une page vient d'être traitée : rester sur
          // une page vide laisserait croire que la file l'est.
          if (this.rows.length === 0 && this.page > 1) {
            this.page--;
            this.lastKnownPage = this.page;
            this.loadPage();
          }
        },
        error: () => {
          this.hasError = true;
          this.rows = [];
        },
      });
  }

  /** Un filtre qui change invalide la position : on repart de la première page. */
  private toFirstPage(): void {
    this.page = 1;
    this.lastKnownPage = null;
  }

  /**
   * Pose la recherche. Les compteurs en dépendent : ceux de l'ancienne
   * recherche sont retirés aussitôt, pour que la pagination ne s'appuie pas
   * sur un total qui décrit une autre liste en attendant les nouveaux.
   */
  private setSearch(term: string): void {
    if (term.trim() === this.search.trim()) {
      this.search = term;
      this.loadPage();
      return;
    }
    this.search = term;
    this.counts = null;
    this.load();
  }

  onStatusFilter(status: StatusFilter): void {
    this.statusFilter = status;
    this.toFirstPage();
    this.loadPage();
  }

  onSearchChange(term: string): void {
    this.toFirstPage();
    this.setSearch(term);
  }

  resetFilters(): void {
    this.statusFilter = 'awaiting_review';
    this.toFirstPage();
    this.setSearch('');
  }

  onPage(page: number): void {
    this.page = page;
    this.loadPage();
  }

  onPageSize(size: number): void {
    this.pageSize = size;
    this.toFirstPage();
    this.loadPage();
  }

  get activeFilterCount(): number {
    return (this.statusFilter === 'awaiting_review' ? 0 : 1) + (this.search ? 1 : 0);
  }

  /**
   * Le responsable légal en une ligne, ou un tiret.
   *
   * <p>Concaténer dans le gabarit reviendrait à écrire « null null » sur une
   * demande antérieure à la collecte de cette information — et à faire échouer
   * la compilation, `strictTemplates` refusant d'additionner un `string | null`.</p>
   */
  parentName(r: AdminRegistrationDetail): string {
    const nom = [r.parentFirstName, r.parentLastName].filter(Boolean).join(' ');
    return nom || '—';
  }

  statusLabelKey(status: RegistrationStatus | 'all'): string {
    return `REGISTRATIONS.STATUS_${status.toUpperCase()}`;
  }

  // ── Détail ────────────────────────────────────────────────────────────

  openDetail(row: AdminRegistrationListItem): void {
    this.isDetailOpen = true;
    this.isDetailLoading = true;
    this.detail = null;
    this.reason = '';
    this.pendingDecision = null;

    this.registrations
      .getById(row.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isDetailLoading = false)),
      )
      .subscribe({
        next: (res) => (this.detail = res.data),
        error: (err) => {
          this.isDetailOpen = false;
          this.swal.error(this.messageOf(err, 'REGISTRATIONS.ERROR_DETAIL'));
        },
      });
  }

  closeDetail(): void {
    this.isDetailOpen = false;
    this.detail = null;
    this.pendingDecision = null;
    this.reason = '';
  }

  /** Libellé i18n d'un acte du journal. */
  auditLabelKey(action: string): string {
    return `REGISTRATIONS.AUDIT_${action.toUpperCase()}`;
  }

  /** Icône par acte — l'œil repère l'exonération dans une liste. */
  auditIcon(action: string): string {
    switch (action) {
      case 'submitted':
        return 'fa-paper-plane';
      case 'proof_submitted':
        return 'fa-paperclip';
      case 'validated':
        return 'fa-circle-check';
      case 'rejected':
        return 'fa-circle-xmark';
      case 'fee_waived':
        return 'fa-hand-holding-heart';
      case 'activation_resent':
        return 'fa-envelope';
      default:
        return 'fa-circle-info';
    }
  }

  /** Le justificatif est une image : il s'affiche ; un PDF s'ouvre à part. */
  get proofIsImage(): boolean {
    return this.detail?.proofContentType?.startsWith('image/') ?? false;
  }

  // ── Décisions ─────────────────────────────────────────────────────────

  approve(): void {
    if (!this.detail) return;
    const id = this.detail.id;

    this.swal.confirm(
      this.translate.instant('REGISTRATIONS.CONFIRM_APPROVE'),
      () => {
        this.isActing = true;
        this.registrations
          .approve(id)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isActing = false)),
          )
          .subscribe({
            next: (res) => {
              this.detail = res.data;
              this.swal.success(this.translate.instant('REGISTRATIONS.APPROVED'));
              this.load();
            },
            error: (err) => this.swal.error(this.messageOf(err, 'REGISTRATIONS.ERROR_APPROVE')),
          });
      },
    );
  }

  /** Ouvre la saisie de motif : un rejet comme une exonération se justifie. */
  startDecision(kind: 'reject' | 'waive'): void {
    this.pendingDecision = kind;
    this.reason = '';
  }

  cancelDecision(): void {
    this.pendingDecision = null;
    this.reason = '';
  }

  get reasonTooShort(): boolean {
    return this.reason.trim().length < 5;
  }

  confirmDecision(): void {
    if (!this.detail || !this.pendingDecision || this.reasonTooShort) return;

    const id = this.detail.id;
    const kind = this.pendingDecision;
    const request$ =
      kind === 'reject'
        ? this.registrations.reject(id, this.reason.trim())
        : this.registrations.waiveFee(id, this.reason.trim());

    this.isActing = true;
    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isActing = false)),
      )
      .subscribe({
        next: (res) => {
          this.detail = res.data;
          this.pendingDecision = null;
          this.reason = '';
          this.swal.success(
            this.translate.instant(
              kind === 'reject' ? 'REGISTRATIONS.REJECTED' : 'REGISTRATIONS.WAIVED',
            ),
          );
          this.load();
        },
        error: (err) =>
          this.swal.error(
            this.messageOf(
              err,
              kind === 'reject' ? 'REGISTRATIONS.ERROR_REJECT' : 'REGISTRATIONS.ERROR_WAIVE',
            ),
          ),
      });
  }

  resendActivation(): void {
    if (!this.detail) return;

    this.isActing = true;
    this.registrations
      .resendActivation(this.detail.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isActing = false)),
      )
      .subscribe({
        next: () => this.swal.success(this.translate.instant('REGISTRATIONS.ACTIVATION_SENT')),
        error: (err) => this.swal.error(this.messageOf(err, 'REGISTRATIONS.ERROR_ACTIVATION')),
      });
  }

  /** Le message du back est plus précis que le nôtre : on le préfère. */
  private messageOf(err: unknown, fallbackKey: string): string {
    const message = (err as { error?: { message?: string } })?.error?.message;
    return message || this.translate.instant(fallbackKey);
  }
}
